import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Baby, UserRound } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function OpenLabourPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/maternity');
  const [form, setForm] = useState({ patientId: params.get('patientId') ?? '', gravida: '', para: '', gestationWeeks: '' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/maternity/deliveries', {
        patientId: form.patientId,
        gravida: form.gravida ? Number(form.gravida) : undefined,
        para: form.para ? Number(form.para) : undefined,
        gestationWeeks: form.gestationWeeks ? Number(form.gestationWeeks) : undefined,
      }),
    onSuccess: (delivery) => {
      toast.success('Labour record opened');
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      navigate(`/maternity?delivery=${delivery.id}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !create.isSuccess && (form.patientId !== (params.get('patientId') ?? '') || form.gravida !== '' || form.para !== '' || form.gestationWeeks !== '');

  return (
    <FormScreen
      title="Open a labour record"
      description="One open episode per mother — the partograph is a single timeline"
      icon={Baby}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!form.patientId) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('opd', 'patient')} icon={UserRound} columns={1}>
        <Field label="Mother" required>
          <PatientPicker value={form.patientId} onChange={(patientId) => setForm({ ...form, patientId })} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'details')} icon={Baby} columns={3}>
        <Field label="Gravida">
          <Input type="number" min={1} value={form.gravida} onChange={(e) => setForm({ ...form, gravida: e.target.value })} />
        </Field>
        <Field label="Para">
          <Input type="number" min={0} value={form.para} onChange={(e) => setForm({ ...form, para: e.target.value })} />
        </Field>
        <Field label="Gestation (weeks)">
          <Input type="number" step="0.1" min={20} max={45} value={form.gestationWeeks} onChange={(e) => setForm({ ...form, gestationWeeks: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Open" loading={create.isPending} incomplete={!form.patientId} cancelTo={returnTo} />
    </FormScreen>
  );
}
